var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { memo, useMemo } from 'react';
import { useCreateGiveawayForm } from 'root/src/client/v2/giveaways/features/CreateGiveawayModal/hooks/useCreateGiveawayForm';
import { FormProvider, useFormContext } from 'react-hook-form';
import { TimeDurationField } from 'root/src/client/v2/giveaways/features/CreateGiveawayModal/components/TimeDurationField';
import { SchedulerPreview } from 'root/src/client/v2/giveaways/features/CreateGiveawayModal/components/SchedulerPreview';
import { ButtonNew, ButtonNewVariant } from 'root/src/client/v2/common/components/form/ButtonNew';
import LoadingCircles from 'root/src/client/v2/common/components/LoadingCircles';
import { useMutation } from '@tanstack/react-query';
import { GiveawaySchedulerConfigStatus, } from '@daredrop-com/rewards-api-contract';
import { CreateGiveawayFormContext, useCreateGiveawayFormContext, } from 'root/src/client/v2/giveaways/features/CreateGiveawayModal/contexts/createGiveawayForm.context';
import { AdvancedSettingsSection } from 'root/src/client/v2/giveaways/features/CreateGiveawayModal/components/AdvancedSettingsSection';
export const CreateGiveawayForm = memo((props) => {
    var _a;
    const { onSubmit, onCancel, giveaway, scheduler, giveawayConfig, } = props;
    const submitMutation = useMutation({
        mutationKey: ['giveaway', 'submit'],
        mutationFn: (formData) => __awaiter(void 0, void 0, void 0, function* () {
            const data = Object.assign(Object.assign({}, formData), { repeatTimes: formData.isRepeatingEnabled ? formData.repeatTimes : null, maxDailyUserWins: (formData === null || formData === void 0 ? void 0 : formData.isMaxDailyWinsEnabled) ? formData.maxDailyUserWins : null });
            return onSubmit(data);
        }),
    });
    const form = useCreateGiveawayForm({
        defaultValues: Object.assign(Object.assign({}, scheduler), { isRepeatingEnabled: Boolean(scheduler === null || scheduler === void 0 ? void 0 : scheduler.repeatTimes), isMaxDailyWinsEnabled: Boolean(giveawayConfig === null || giveawayConfig === void 0 ? void 0 : giveawayConfig.maxDailyUserWins), maxWinners: giveawayConfig === null || giveawayConfig === void 0 ? void 0 : giveawayConfig.maxWinners, maxDailyUserWins: giveawayConfig === null || giveawayConfig === void 0 ? void 0 : giveawayConfig.maxDailyUserWins, isTwitchSubscribersOnly: giveawayConfig === null || giveawayConfig === void 0 ? void 0 : giveawayConfig.isTwitchSubscribersOnly, voucherCountPerWinner: (_a = giveawayConfig === null || giveawayConfig === void 0 ? void 0 : giveawayConfig.voucherCountPerWinner) !== null && _a !== void 0 ? _a : undefined }),
    });
    const createGiveawayFormContextValue = useMemo(() => {
        return {
            onCancel,
            giveaway,
            scheduler,
        };
    }, [onCancel, giveaway, scheduler]);
    return (<CreateGiveawayFormContext.Provider value={createGiveawayFormContextValue}>
			<FormProvider {...form}>
				<form onSubmit={form.handleSubmit(data => submitMutation.mutateAsync(data))} className="flex flex-initial justify-center items-center flex-col gap-4 py-2">
					<TimeDurationField />
					<AdvancedSettingsSection />
					<Spacer />
					<SchedulerPreview />
					<Buttons />
					<InfoText />
					<FormError />
				</form>
			</FormProvider>
		</CreateGiveawayFormContext.Provider>);
});
const Buttons = memo(() => {
    const { giveaway, scheduler } = useCreateGiveawayFormContext();
    if ((scheduler === null || scheduler === void 0 ? void 0 : scheduler.status) === GiveawaySchedulerConfigStatus.Active) {
        return (<div className="w-[90%] flex flex-col sm:flex-row justify-center items-center gap-4">
				<EditGiveawayButton />
				<CancelGiveawayButton />
			</div>);
    }
    if (giveaway) {
        return (<div className="w-[90%] flex justify-center items-center gap-4">
				<CreateScheduleButton />
			</div>);
    }
    return (<div className="w-[90%] flex justify-center items-center gap-4">
			<CreateGiveawayButton />
		</div>);
});
const Spacer = memo(() => {
    return <div className="flex flex-initial w-3/4 h-[1px] rounded bg-dark-5"/>;
});
const FormError = memo(() => {
    const form = useFormContext();
    const error = form.formState.errors.durationTime;
    if (!error) {
        return null;
    }
    return <p className="text-sm text-pink">{error.message}</p>;
});
const CreateGiveawayButton = memo(() => {
    const form = useFormContext();
    return (<ButtonNew className="font-bold capitalize w-full h-12" disabled={form.formState.isSubmitting} type="submit">
			{form.formState.isSubmitting ? <LoadingCircles width={20} height={20}/> : 'Start giveaway'}
		</ButtonNew>);
});
const CreateScheduleButton = memo(() => {
    const form = useFormContext();
    return (<ButtonNew className="font-bold capitalize w-full" disabled={form.formState.isSubmitting} type="submit">
			{form.formState.isSubmitting ? <LoadingCircles width={20} height={20}/> : 'Create schedule'}
		</ButtonNew>);
});
const CancelGiveawayButton = memo(() => {
    const { onCancel } = useCreateGiveawayFormContext();
    const mutation = useMutation({
        mutationKey: ['giveaway', 'cancel'],
        mutationFn: () => __awaiter(void 0, void 0, void 0, function* () {
            if (onCancel) {
                yield onCancel();
            }
        }),
    });
    return (<ButtonNew onClick={() => mutation.mutateAsync()} variant={ButtonNewVariant.Error} className="font-bold capitalize w-full" disabled={mutation.isPending} type="button">
			{mutation.isPending ? <LoadingCircles width={20} height={20}/> : 'Cancel schedule'}
		</ButtonNew>);
});
const EditGiveawayButton = memo(() => {
    const form = useFormContext();
    return (<ButtonNew variant={ButtonNewVariant.Primary} className="font-bold capitalize w-full" type="submit" disabled={form.formState.isSubmitting}>
			{form.formState.isSubmitting ? <LoadingCircles width={20} height={20}/> : 'Save schedule'}
		</ButtonNew>);
});
const InfoText = memo(() => {
    return (<span className="text-xs">Your changes will take effect in the next giveaway</span>);
});
